import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

function ScoringRules() {
  const { user } = useAuth();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    eventType: 'on_time_delivery',
    points: 0,
    isActive: true
  });

  const eventTypes = [
    { value: 'on_time_delivery', label: 'Zamanında Teslimat', defaultPoints: 15 },
    { value: 'early_delivery', label: 'Erken Teslimat', defaultPoints: 20 },
    { value: 'late_delivery', label: 'Geç Teslimat (gün başına)', defaultPoints: -5 },
    { value: 'qc_approved', label: 'QC Onayı', defaultPoints: 20 },
    { value: 'qc_rejected', label: 'QC Reddi', defaultPoints: -30 },
    { value: 'extension_requested', label: 'Süre Uzatma Talebi', defaultPoints: -10 },
    { value: 'process_completed', label: 'Proses Tamamlama', defaultPoints: 5 },
    { value: 'custom', label: 'Özel', defaultPoints: 0 }
  ];

  useEffect(() => {
    fetchRules();
  }, []);

  const fetchRules = async () => {
    try {
      const response = await api.getScoringRules();
      setRules(response.data);
    } catch (error) {
      console.error('Puanlama kuralları yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingRule) {
        await api.updateScoringRule(editingRule.id, formData);
      } else {
        await api.createScoringRule(formData);
      }
      fetchRules();
      closeModal();
    } catch (error) {
      alert(error.response?.data?.message || 'İşlem başarısız');
    }
  };

  const handleDelete = async (id) => {
    if (user.userType !== 'admin') {
      alert('Sadece admin silme yapabilir!');
      return;
    }
    if (window.confirm('Bu kuralı silmek istediğinize emin misiniz?')) {
      try {
        await api.deleteScoringRule(id);
        fetchRules();
      } catch (error) {
        alert(error.response?.data?.message || 'Silme başarısız');
      }
    }
  };

  const openModal = (rule = null) => {
    if (rule) {
      setEditingRule(rule);
      setFormData({
        name: rule.name,
        description: rule.description || '',
        eventType: rule.eventType,
        points: rule.points,
        isActive: rule.isActive
      });
    } else {
      setEditingRule(null);
      setFormData({
        name: '',
        description: '',
        eventType: 'on_time_delivery',
        points: 15,
        isActive: true
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingRule(null);
  };

  const handleEventTypeChange = (e) => {
    const eventType = e.target.value;
    const eventConfig = eventTypes.find(et => et.value === eventType);
    setFormData({
      ...formData,
      eventType,
      points: eventConfig?.defaultPoints || 0
    });
  };

  const getEventTypeLabel = (type) => {
    const eventConfig = eventTypes.find(et => et.value === type);
    return eventConfig?.label || type;
  };

  const getPointsColor = (points) => {
    if (points > 0) return 'text-green-600';
    if (points < 0) return 'text-red-600';
    return 'text-gray-600';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Puanlama Kuralları</h1>
          <p className="text-gray-600 mt-1">İmalatçı performans puanlama sistemini yönetin</p>
        </div>
        <button
          onClick={() => openModal()}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          + Yeni Kural
        </button>
      </div>

      {/* Info Card */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-semibold text-blue-900 mb-2">Puanlama Sistemi Hakkında</h3>
        <p className="text-sm text-blue-800">
          Her imalatçı 100 puan üzerinden değerlendirilir. Puanlar işlem bazlı eklenir/çıkarılır.
          İşlemler otomatik olarak tetiklenir (teslimat, QC sonucu, vb.).
        </p>
      </div>

      {/* Rules Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Kural Adı
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Olay Tipi
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Puan
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Durum
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                İşlemler
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {rules.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                  Henüz kural eklenmemiş
                </td>
              </tr>
            ) : (
              rules.map((rule) => (
                <tr key={rule.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{rule.name}</div>
                      {rule.description && (
                        <div className="text-sm text-gray-500">{rule.description}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">
                      {getEventTypeLabel(rule.eventType)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`text-lg font-bold ${getPointsColor(rule.points)}`}>
                      {rule.points > 0 ? '+' : ''}{rule.points}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      rule.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {rule.isActive ? 'Aktif' : 'Pasif'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => openModal(rule)}
                      className="text-blue-600 hover:text-blue-900 mr-3"
                    >
                      Düzenle
                    </button>
                    {user.userType === 'admin' && (
                      <button
                        onClick={() => handleDelete(rule.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Sil
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Default Rules Info */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Varsayılan Puanlama Tablosu</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {eventTypes.filter(et => et.value !== 'custom').map((et) => (
            <div key={et.value} className="p-3 bg-gray-50 rounded-lg">
              <div className="text-sm text-gray-600">{et.label}</div>
              <div className={`text-lg font-bold ${getPointsColor(et.defaultPoints)}`}>
                {et.defaultPoints > 0 ? '+' : ''}{et.defaultPoints} Puan
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">
              {editingRule ? 'Kural Düzenle' : 'Yeni Puanlama Kuralı'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Kural Adı
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Örn: Zamanında Teslimat Bonusu"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Açıklama
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Kural açıklaması..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Olay Tipi
                </label>
                <select
                  value={formData.eventType}
                  onChange={handleEventTypeChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {eventTypes.map((et) => (
                    <option key={et.value} value={et.value}>
                      {et.label} ({et.defaultPoints > 0 ? '+' : ''}{et.defaultPoints})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Puan
                </label>
                <input
                  type="number"
                  value={formData.points}
                  onChange={(e) => setFormData({ ...formData, points: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Pozitif değer ekler, negatif değer çıkarır
                </p>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="isActive" className="ml-2 text-sm text-gray-700">
                  Aktif
                </label>
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {editingRule ? 'Güncelle' : 'Oluştur'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default ScoringRules;
